/**
 * QuickFollowUpDialog - Small dialog for scheduling a follow-up without leaving the list view
 *
 * Can be triggered from the leads list table row to quickly schedule a follow-up
 * without navigating to the lead detail page.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CalendarPlus, Loader2 } from 'lucide-react'
import { useCreateFollowUp } from '@/hooks/useLeads'
import { useToast } from '@/hooks/use-toast'

interface QuickFollowUpDialogProps {
  leadId: string
  leadName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function QuickFollowUpDialog({
  leadId,
  leadName,
  open,
  onOpenChange,
}: QuickFollowUpDialogProps) {
  const { toast } = useToast()
  const createFollowUp = useCreateFollowUp()

  const [title, setTitle] = useState('')
  const [dueAt, setDueAt] = useState('')

  // Set a sensible default for the datetime-local input (tomorrow at noon)
  const getDefaultDateTime = () => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(12, 0, 0, 0)
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && !dueAt) {
      setDueAt(getDefaultDateTime())
    }
    if (!newOpen) {
      setTitle('')
      setDueAt('')
    }
    onOpenChange(newOpen)
  }

  const handleSubmit = async () => {
    if (!title.trim() || !dueAt) return

    try {
      await createFollowUp.mutateAsync({
        leadId,
        title: title.trim(),
        dueAt: new Date(dueAt).toISOString(),
      })

      toast({
        title: 'Follow-up scheduled',
        description: `Follow-up "${title.trim()}" scheduled for ${new Date(dueAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}.`,
      })

      setTitle('')
      setDueAt('')
      onOpenChange(false)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to schedule follow-up.',
        variant: 'destructive',
      })
    }
  }

  const isValid = title.trim().length > 0 && dueAt.length > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-blue-600" />
            Schedule Follow-Up
          </DialogTitle>
          <DialogDescription>
            Schedule a follow-up for <span className="font-medium text-foreground">{leadName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="followup-title">Title</Label>
            <Input
              id="followup-title"
              placeholder="e.g., Call back about pricing"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="followup-due">Due Date & Time</Label>
            <Input
              id="followup-due"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createFollowUp.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || createFollowUp.isPending}
          >
            {createFollowUp.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
